import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import apiClient from '../../services/api';

const TILES = [
  { char: '.', label: 'Air', color: 'bg-slate-900' },
  { char: '#', label: 'Block', color: 'bg-slate-500' },
  { char: 'C', label: 'Coin', color: 'bg-yellow-500' },
  { char: 'E', label: 'Walker', color: 'bg-red-500' },
  { char: 'J', label: 'Jumper', color: 'bg-orange-500' },
  { char: 'S', label: 'Spike', color: 'bg-gray-400' },
  { char: 'P', label: 'Spawn', color: 'bg-green-500' },
  { char: 'F', label: 'Flag', color: 'bg-purple-500' },
];

const tileColor = (c) => (TILES.find((t) => t.char === c) || TILES[0]).color;

export const TilemapEditor = ({ open, onClose, session, onSaved }) => {
  const config = session?.metadata?.engine_config;
  const [levelIdx, setLevelIdx] = useState(0);
  const [rows, setRows] = useState([]);
  const [brush, setBrush] = useState('#');
  const [painting, setPainting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && config?.levels?.[levelIdx]) {
      setRows(config.levels[levelIdx].tilemap.map((r) => r));
      setError(null);
    }
  }, [open, levelIdx, config]);

  if (!config) return null;
  const levels = config.levels || [];

  const paint = (y, x) => {
    setRows((prev) => {
      const next = [...prev];
      const row = next[y];
      next[y] = row.substring(0, x) + brush + row.substring(x + 1);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const newConfig = JSON.parse(JSON.stringify(config));
      newConfig.levels[levelIdx].tilemap = rows;
      const { data } = await apiClient.put(`/ai/sessions/${session.session_id}/engine-config`, {
        config: newConfig,
      });
      onSaved(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save level');
    } finally {
      setSaving(false);
    }
  };

  const maxW = rows.length ? Math.max(...rows.map((r) => r.length)) : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="tilemap-editor-dialog" className="bg-gray-900 border border-purple-500/40 max-w-5xl">
        <DialogHeader>
          <DialogTitle className="text-white">Level Editor</DialogTitle>
          <DialogDescription className="text-gray-400 text-xs">
            Click or drag on the grid to paint tiles. Each level needs one spawn (P) and at least one flag (F).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-gray-400">Level:</span>
            {levels.map((lvl, i) => (
              <Button
                key={i}
                size="sm"
                variant={i === levelIdx ? 'default' : 'outline'}
                className={i === levelIdx ? 'bg-purple-600 text-white' : 'border-white/20 text-gray-300'}
                onClick={() => setLevelIdx(i)}
              >
                {i + 1}. {lvl.name || `Level ${i + 1}`}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-gray-400">Brush:</span>
            {TILES.map((t) => (
              <button
                key={t.char}
                type="button"
                data-testid={`tile-brush-${t.label.toLowerCase()}`}
                onClick={() => setBrush(t.char)}
                className={`px-2 py-1 rounded text-[11px] font-mono border ${
                  brush === t.char ? 'border-purple-400 text-white bg-purple-600/40' : 'border-white/10 text-gray-300 bg-black/40'
                }`}
              >
                <span className={`inline-block w-3 h-3 mr-1 rounded-sm align-middle ${t.color}`} />
                {t.label}
              </button>
            ))}
          </div>
          <div
            className="overflow-auto max-h-[380px] border border-white/10 rounded-lg bg-black/60 p-2"
            onMouseLeave={() => setPainting(false)}
          >
            <div style={{ width: maxW * 18 }}>
              {rows.map((row, y) => (
                <div key={y} className="flex" style={{ height: 18 }}>
                  {Array.from({ length: maxW }).map((_, x) => {
                    const c = row[x] || '.';
                    return (
                      <div
                        key={x}
                        role="presentation"
                        onMouseDown={() => { setPainting(true); paint(y, x); }}
                        onMouseUp={() => setPainting(false)}
                        onMouseEnter={() => painting && paint(y, x)}
                        className={`cursor-crosshair border border-white/5 ${tileColor(c)}`}
                        style={{ width: 18, height: 18, flexShrink: 0 }}
                        title={`(${x},${y}) ${c}`}
                      >
                        {c !== '.' && c !== '#' && (
                          <span className="text-[9px] text-black font-bold leading-[18px] block text-center select-none">{c}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          {error && <p className="text-xs text-red-400">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="border-white/20 text-gray-300" onClick={onClose}>Cancel</Button>
            <Button
              data-testid="tilemap-save-btn"
              onClick={handleSave}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {saving ? 'Saving...' : 'Save Level'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
